import {
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from '@vrplatform/query-params';
import { hasValue } from '@vrplatform/utils';
import {
  useAuditEffectsModalFilter,
  useAuditEffectsModalWhere,
} from './useAuditEffectsModal';
import type { api } from '@vrplatform/data';

type AuditEffectsApiFilter = Required<
  NonNullable<api['/audit/effects']['get']['parameters']['query']>
>;

export const AUDIT_EFFECT_STATUSES: Record<
  AuditEffectsApiFilter['status'],
  string
> = {
  pending: 'Pending',
  claimed: 'Claimed',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  dead_letter: 'Dead Letter',
};

export const AUDIT_EFFECT_TYPES: Record<
  AuditEffectsApiFilter['effectType'],
  string
> = {
  CREATE_RECURRING_TRANSACTION_INSTANCE:
    'Create Recurring Transaction Instance',
  DELETE_TRANSACTION_ATTACHMENT_BLOB: 'Delete Transaction Attachment Blob',
  REFRESH_RESERVATION_JOURNAL: 'Refresh Reservation Journal',
  REFRESH_TRANSACTION_JOURNAL: 'Refresh Transaction Journal',
  UPDATE_RESERVATION_LINKABLES: 'Update Reservation Linkables',
  UPDATE_RESERVATION_PAYMENTS: 'Update Reservation Payments',
  TENANT_CLERK_ALLOWED_IDS_SYNC: 'Team Clerk Allowed Ids Sync',
  TENANT_HYPERLINE_SYNC: 'Team Hyperline Sync',
  TENANT_LINEAR_WEBHOOK: 'Team Linear Webhook',
  TENANT_STATUS_TRACK: 'Team Status Track',
  TENANT_SUSPENSION_EMAIL: 'Team Suspension Email',
  LINK_JOURNAL_TO_OWNERSHIP_PERIOD: 'Link Journal to Ownership Period',
  RECALCULATE_BANK_RECORD_BALANCES: 'Recalculate Bank Record Balances',
  SEND_STATEMENT_NOTIFICATIONS: 'Send Statement Notifications',
  JOURNAL_ENTRY_SET_ACTIVE: 'Journal Entry Status Update to Active',
  JOURNAL_ENTRY_SET_INACTIVE: 'Journal Entry Status Update to Inactive',
  TENANT_INIT: 'Team Initialization',
  MAP_DEPOSIT_TRANSACTIONS_BY_LAST4: 'Map Deposits by Last4',
  BANK_ACCOUNT_CREATED_TRACK: 'Tracked Bank Account Created',
  CALENDAR_UNBLOCK_ACTION: 'Calendar Unblock Action',
};

export const AUDIT_EFFECT_ENTITY_TYPES: Record<
  AuditEffectsApiFilter['entityType'],
  string
> = {
  recurringTransactionTemplate: 'Recurring Expense Template',
  reservation: 'Reservation',
  tenant: 'Team',
  transaction: 'Deposit/Expense/Transfer',
  transactionAttachment: 'Transaction Attachment',
  listingOwnershipPeriod: 'Listing Ownership Period',
  account: 'Account',
  ownerStatement: 'Owner Statement',
  journalEntry: 'Journal Entry',
};

const STATUSES = Object.keys(
  AUDIT_EFFECT_STATUSES
) as AuditEffectsApiFilter['status'][];
const EFFECT_KEYS = Object.keys(
  AUDIT_EFFECT_TYPES
) as AuditEffectsApiFilter['effectType'][];
const ENTITY_KEYS = Object.keys(
  AUDIT_EFFECT_ENTITY_TYPES
) as AuditEffectsApiFilter['entityType'][];

export const useAuditEffectsTableFilter = () => {
  'use memo';

  const [queryParams, setQueryParams] = useQueryStates({
    search: parseAsString,
    status: parseAsStringLiteral(STATUSES),
    effectType: parseAsStringLiteral(EFFECT_KEYS),
    entityType: parseAsStringLiteral(ENTITY_KEYS),
  });

  const { filter: state, setFilter: setState } = useAuditEffectsModalFilter();
  const where = useAuditEffectsModalWhere();
  const isModalOpened = !!where;

  const isFiltered = isModalOpened
    ? Object.values((state as object) ?? {}).some(hasValue)
    : Object.values(queryParams).some(hasValue);

  return {
    filter: isModalOpened ? (state as typeof queryParams) : queryParams,
    isFiltered,
    setFilter: (filter: Partial<typeof state>) => {
      if (isModalOpened) {
        setState(filter);
      } else {
        setQueryParams(filter as Partial<typeof queryParams>);
      }
    },
    reset: () => {
      const initial: Record<keyof typeof queryParams, null> = {
        search: null,
        status: null,
        effectType: null,
        entityType: null,
      };

      if (isModalOpened) {
        setState(initial);
      } else {
        setQueryParams(null);
      }
    },
  };
};
